import useNavigationStore from '@src/stores/navigationStore';
import useRegistrationStore from '@src/stores/registrationStore';

import styles from './FiscalAddress.module.scss';
import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import BackButton from '@src/components/_shared/BackButton';
import Container from '@src/components/_shared/Container';
import { getReferencialData } from '@src/api/Referencial';
import { useMutation, useQuery } from '@tanstack/react-query';
import CountryDropdown from '@src/components/_shared/UI/CountryDropdownList';
import { Form, Formik } from 'formik';
import AddressSearch from '@src/components/_shared/UI/AddressSearch';
import { useEffect, useState } from 'react';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import Input from '@src/components/_shared/UI/Input';
import { AddressEnumType, AddressType, SearchAddressType } from '@src/utilities/types';
import { personAdress, updatePersonAddresses } from '@src/api/Registration';
import ValidateChoiceButton from '@src/components/_shared/UI/ValidateChoiceButton';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import Loader from '@src/components/_shared/UI/Loader';
import { handleApiError } from '@src/utilities/functions';
import { addressesSchema } from '@src/utilities/schemas';

/**
    @todo: Ajouter schemas de validaton sur champs
**/
export default function FiscalAddress() {
    const { setSection, setBlock } = useNavigationStore();
    const { setField, registration } = useRegistrationStore();
    const [fiscalAddress, setFiscalAddress] = useState<AddressType>({ ...registration.addresses.find(address => address.address_type === AddressEnumType.fiscale)} as AddressType);
    const [notFound, setNotFound] = useState<boolean>(false);
    const [sameAddress, setSameAddress] = useState<boolean>(false);
    const [fiscalNotIdentical, setFiscalNotIdentical] = useState<boolean>(false);
    const [hasOtherCountry, setHasOtherCountry] = useState<boolean>(false);
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [displaySearch, setDisplaySearch] = useState<boolean>(false);

    const homeAddress = registration.addresses.find(
        (address) => address.address_type === AddressEnumType.postale
    );

    const _onGoBack = () => {
        setSection(2)
        setBlock('HomeAddress')
    };

    const { data } = useQuery({
        queryKey: [`getCountriesData`],
        queryFn: () => getReferencialData({type: 'countries'})
    })

    const { mutate, isPending } = useMutation({
        mutationFn: () => personAdress({ addressValue: fiscalAddress, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id }),
        onSuccess: (data: any) => {
            registration.addresses.filter((obj) => {
                return obj.address_type === data.type ? obj.id = data.id : null;
            });
            setBlock('Birth');
            setSameAddress(false);
        },
        onError: (error: any) => handleApiError(error)
    });

    const { mutate: MutateUpdateAddresses, isPending: isPendingUpdateAddresses} = useMutation({
        mutationFn: () => updatePersonAddresses({ addressesValue: fiscalAddress, id: registration.addresses[1].id, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id }),
        onSuccess: () => {
            setSameAddress(false);
            setBlock('Birth');
        },
        onError: (error: any) => handleApiError(error)
    });

    const _onAssignSameAddress = () => {
        if (homeAddress) {
            const fiscalAddress = registration.addresses.find((address) => address.address_type === AddressEnumType.fiscale) as AddressType;
            const updatedFiscalAddress = {...homeAddress, address_type: AddressEnumType.fiscale, id: fiscalAddress.id || ''};
            setFiscalAddress(updatedFiscalAddress);
            const updatedAddresses = registration.addresses.map((address) => address.address_type === AddressEnumType.fiscale ? { ...updatedFiscalAddress } : address);
            setField('addresses', updatedAddresses);
            setSameAddress(true);
        }
    }

    useEffect(() => {
        if (sameAddress) {
            if (fiscalAddress.id) {
                MutateUpdateAddresses();
            } else {
                mutate();
            }
        }
    }, [sameAddress])

    const handleSelectCountry = (value: string) => {
        setFiscalAddress({...fiscalAddress, country: value});
    };

    const _onSelectExistingAddress = (searchAddress: SearchAddressType) => {
        const regex = /^([0-9]+)\s*(b|bis|t|ter|q|quater)?$/i;
        const result = (searchAddress.housenumber || '').trim().match(regex);
        const number = result && result[1] ? result[1] : '';
        const extension = result && result[2] ? result[2] : '';
        setFiscalAddress((fiscalAddress: any) => ({
            ...fiscalAddress,
            street_number: number || searchAddress.housenumber,
            street_extension: extension.charAt(0).toUpperCase() || '',
            street_name: searchAddress.street,
            city: {
                ...fiscalAddress.city,
                zipcode: searchAddress.postcode,
                name: searchAddress.city,
            }
        }));
    }

    const _onValidateFiscalAddress = () => {
        registration.addresses.filter(obj => obj.address_type === AddressEnumType.fiscale).forEach((obj) => {
            obj.street_number = fiscalAddress.street_number;
            obj.street_extension = fiscalAddress.street_extension;
            obj.street_name_further = fiscalAddress.street_name_further;
            obj.street_name = fiscalAddress.street_name;
            obj.city.zipcode = fiscalAddress.city.zipcode;
            obj.city.name = fiscalAddress.city.name;
            obj.country = fiscalAddress.country;
        });
        if (fiscalAddress.id) {
            MutateUpdateAddresses();
        } else {
            mutate();
        }
    }

    useEffect(() => {
        if (submitted) {
            _onValidateFiscalAddress();
            setSubmitted(false);
        }
    }, [submitted, fiscalAddress]);

    const _onChooseOtherCountry = () => {
        setHasOtherCountry(true)
        setDisplaySearch(true)
    }

    const _onSetSameCountry = () => {
        const homeAddress = registration.addresses.find(
            (address) => address.address_type === AddressEnumType.postale
        );
        if (homeAddress) {
            setFiscalAddress({...fiscalAddress, country: homeAddress.country});
        }
        setHasOtherCountry(false);
        setDisplaySearch(true);
    }

    if (fiscalNotIdentical) {
        return (
            <Container>
                <BackButton onClick={_onGoBack}/>
                <BlockTitle title="Avez-vous un autre pays de résidence fiscale" />
                <Subtitle content="Indiquez si vous avez un autre pays de résidence fiscale" />
                <div className={styles.buttons}>
                    <ValidateChoiceButton title="Oui" selected={hasOtherCountry && displaySearch} onValidate={_onChooseOtherCountry} />
                    <ValidateChoiceButton title="Non" selected={displaySearch && !hasOtherCountry} onValidate={_onSetSameCountry} />
                </div>
                {(hasOtherCountry) && data && (
                    <>
                        <CountryDropdown
                            title="Sélectionnez un pays"
                            width="100%"
                            options={data}
                            selected={fiscalAddress.country}
                            onSelect={handleSelectCountry}
                        />
                    </>
                )}
                {fiscalAddress.country ?
                    displaySearch && !notFound && fiscalAddress.country === 'FR' ?
                        <>
                            <AddressSearch {...{selectedAddress: fiscalAddress, setAddress: setFiscalAddress, _onSelectExistingAddress, setNotFound}}/>
                            <ValidateButton title="Suivant" disabled={!fiscalAddress.city.zipcode} onClick={_onValidateFiscalAddress} onLoading={isPending || isPendingUpdateAddresses} />
                        </>
                        :
                        <Formik
                            initialValues={{
                                street_number: fiscalAddress.street_number + ' ' + fiscalAddress.street_extension,
                                street_name_further: fiscalAddress.street_name_further,
                                street_name: fiscalAddress.street_name,
                                city: {
                                    name: fiscalAddress.city.name,
                                    zipcode: fiscalAddress.city.zipcode,
                                }
                            }}
                            onSubmit={values => {
                                const regex = /^([0-9]+)\s*(b|bis|t|ter|q|quater)?$/i;
                                const result = (values.street_number || '').trim().match(regex);
                                const number = result && result[1] ? result[1] : '';
                                const extension =  result && result[2] ? result[2] : '';
                                setFiscalAddress((fiscalAddress: any) => ({
                                    ...fiscalAddress,
                                    street_number: number,
                                    street_extension: extension.charAt(0).toUpperCase(),
                                    street_name: values.street_name,
                                    street_name_further: values.street_name_further,
                                    city: values.city,
                                }))
                                setSubmitted(true);
                            }}
                            validationSchema={addressesSchema}
                        >
                            {({ handleChange, handleSubmit, values, errors, isValid, touched, handleBlur }) => {
                                return(
                                    <Form id="fiscalAddressForm" autoComplete="off" className="registerForm">
                                        <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                                            <div style={{width: '30%'}}>
                                                <Input
                                                    title="N°"
                                                    value={values.street_number}
                                                    onChange={handleChange('street_number')}
                                                    id="street_number"
                                                    name="street_number"
                                                    type="text"
                                                    width="100%"
                                                    hasError={(touched.street_number && errors.street_number) as boolean}
                                                    onBlur={handleBlur('street_number')}
                                                />
                                            </div>
                                            <div style={{width: '65%'}}>
                                                <Input
                                                    title="Rue / voie"
                                                    value={values.street_name}
                                                    onChange={handleChange('street_name')}
                                                    placeholder="Ex : rue de la colombe"
                                                    id="street_name"
                                                    name="street_name"
                                                    type="text"
                                                    width="100%"
                                                    clearInput={() => handleChange('street_name')('')}
                                                    hasError={(touched.street_name && errors.street_name) as boolean}
                                                    errorMessage={errors.street_name as string}
                                                    onBlur={handleBlur('street_name')}
                                                />
                                            </div>
                                        </div>
                                        <Input
                                            title="Complément d’adresse (optionnel)"
                                            value={values.street_name_further}
                                            onChange={handleChange('street_name_further')}
                                            placeholder=""
                                            id="street_name_further"
                                            name="street_name_further"
                                            type="text"
                                            width="100%"
                                            clearInput={() => handleChange('street_name_further')('')}
                                            hasError={(touched.street_name_further && errors.street_name_further) as boolean}
                                            onBlur={handleBlur('street_name_further')}
                                        />
                                        <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                                            <div style={{width: '30%'}}>
                                                <Input
                                                    title="Code postal"
                                                    value={values.city.zipcode}
                                                    onChange={handleChange('city.zipcode')}
                                                    id="zipcode"
                                                    name="zipcode"
                                                    type="text"
                                                    width="100%"
                                                    hasError={(touched.city?.zipcode && errors.city?.zipcode) as boolean}
                                                    onBlur={handleBlur('city.zipcode')}
                                                />
                                            </div>
                                            <div style={{width: '65%'}}>
                                                <Input
                                                    title="Ville"
                                                    value={values.city.name}
                                                    onChange={handleChange('city.name')}
                                                    placeholder="Ex : Paris"
                                                    id="cityname"
                                                    name="cityname"
                                                    type="text"
                                                    width="100%"
                                                    clearInput={() => handleChange('city.name')('')}
                                                    hasError={(touched.city?.name && errors.city?.name) as boolean}
                                                    errorMessage={errors.city?.name as string}
                                                    onBlur={handleBlur('city.name')}
                                                />
                                            </div>
                                        </div>
                                        <ValidateButton title="Continuer" onClick={handleSubmit} disabled={!isValid} onLoading={isPending} />
                                    </Form>
                                )}}
                        </Formik>
                    : null}
            </Container>
        );
    }

    return (
        <Container>
            <BackButton onClick={_onGoBack}/>
            <BlockTitle title="Votre adresse fiscale est la même que celle de votre domicile ? " />
            <Subtitle content="Indiquez si votre adresse fiscale est la même que l’adresse de votre domicile" />
            {isPending ?
                <Loader type="SPINNER" />
                :
                <div className={styles.buttons}>
                    <ValidateChoiceButton title="Oui" onValidate={_onAssignSameAddress} />
                    <ValidateChoiceButton title="Non" onValidate={() => setFiscalNotIdentical(true)} />
                </div>
            }
        </Container>
    );
}